import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { papierOeffnen } from "./papieroeffnen";
import * as R from "./routen";
import * as V from "./vorbereitung";
import { mitMappe } from "./mappe";
import { useSitzung } from "../schale/Sitzung";
import { schonWeg } from "./doppelt";
import { ohneVorgang, vorgangStilllegen, vorgangVergessen } from "./verworfen";
import type {
  Bausteinart, Bedienungsart, Belegart, Belegung, DefektArt, DefektSchwere, Passwortwechsel,
  Recht, Rollentafel, Tausch,
} from "./typen";

/* ══════════════════════════════════════════════════════════════════
   Die Haken, mit denen die Masken an die Daten kommen.

   Die Schlüssel stehen an einer Stelle, nicht verstreut in den Masken.
   Sonst heißt derselbe Bestand einmal `["planung"]` und einmal
   `["planung", von, bis]`, und dann macht ein Neuladen nach einer
   Änderung die Hälfte der Ansicht frisch und die andere nicht.
   ══════════════════════════════════════════════════════════════════ */

export const schluessel = {
  ich: ["ich"] as const,
  start: ["start"] as const,
  planung: (von?: string, bis?: string) => ["planung", von ?? "", bis ?? ""] as const,
  /**
   * **Alle Fenster des Bretts — zum Auffrischen, nicht zum Lesen.**
   *
   * `schluessel.planung()` ohne Argumente ergibt `["planung", "", ""]`, und
   * genau das stand bis zum 15.09.2026 in jedem `onSuccess`. TanStack trifft
   * nach Praefix: `["planung","",""]` ist KEIN Praefix von
   * `["planung","2026-09-14","2026-09-18"]` — das Brett lud also nie nach.
   * Luca: *„Wenn ich Urlaub eintrage bei einer Mitarbeiterin, muss es auch
   * live auftauchen. Das taucht dann nicht live auf."* Es tauchte erst beim
   * nächsten Wochenwechsel auf, weil das eine andere Abfrage war.
   *
   * `["planung"]` ist das Präfix aller Fenster — auch des leeren, mit dem die
   * Schnellerfassung fragt.
   */
  planungAlle: ["planung"] as const,
  vorgaenge: ["vorgaenge"] as const,
  /* Unter `vorgaenge`, damit jede Änderung an einem Vorgang den Verlauf
     mitnimmt — TanStack trifft nach Präfix. */
  verlauf: (kunde: string) => ["vorgaenge", "verlauf", kunde] as const,
  vorgang: (nr: string) => ["vorgang", nr] as const,
  angebotsfassungen: (nr: string) => ["vorgang", nr, "fassungen"] as const,
  /* Die Texte je Belegart (0105) — nicht unter `vorgang`: Wer eine Position
     ändert, ändert keinen Text, und ein neuer Text keine Position. */
  belegtexte: (nr: string) => ["belegtexte", nr] as const,
  protokolle: (nr: string) => ["protokolle", nr] as const,
  /* Der Tag des Fahrers. Er steht unter `handy`, damit ein Protokoll ihn
     mit einem Präfix auffrischt — und nicht mit dem halben Rest. */
  handyHeute: ["handy", "heute"] as const,
  /* Die Fotos einer Maschine. Sie kommen aus denselben Protokollen, also
     frischt ein neues Protokoll sie mit auf. */
  geraetefotos: (id: string) => ["geraetefotos", id] as const,
  /* Checklisten und Vorbereitung (0107). Die Geltung steht unter
     `checklisten`, damit jede Änderung im Designer die Vorschau mitnimmt. */
  checklisten: ["checklisten"] as const,
  checklisteGeltung: (typ: string) => ["checklisten", "geltung", typ] as const,
  vorbereitung: (belegung: string) => ["vorbereitung", belegung] as const,
  /* Was an einer Belegung hinausgeht (22.09.2026) — unter `vorbereitung`,
     damit ein getauschtes Anbauteil beide Blätter mitnimmt. */
  uebergabeinhalt: (belegung: string) => ["vorbereitung", "inhalt", belegung] as const,
  vorbereitungenDesVorgangs: (vorgang: string) => ["vorbereitung", "vorgang", vorgang] as const,
  angebote: ["angebote"] as const,
  positionen: (nr: string) => ["positionen", nr] as const,
  /* Unter `positionen`, damit jede Änderung an der Liste sie mitnimmt: Ein
     neuer Zeitraum ist eine andere Frage nach freien Menschen. */
  bedienerauswahl: (nr: string, pos: string) => ["positionen", nr, "bediener", pos] as const,
  kunden: (suche?: string, nur?: string) => ["kunden", suche ?? "", nur ?? ""] as const,
  kunde: (nr: string) => ["kunde", nr] as const,
  maschinen: ["stamm", "maschinen"] as const,
  /* Unter `stamm`: Wer einen Kunden zum Vermieter macht, soll ihn in der
     Geraeteakte sofort waehlen koennen. */
  vermieter: ["stamm", "vermieter"] as const,
  personal: ["stamm", "personal"] as const,
  maschinentypen: ["stamm", "maschinentypen"] as const,
  zuschlaege: ["stamm", "zuschlaege"] as const,
  kataloge: ["stamm", "kataloge"] as const,
  leistungsarten: ["stamm", "leistungsarten"] as const,
  waren: ["stamm", "waren"] as const,
  /* Die Textbausteine stehen NICHT unter `stamm`: Sie werden aus dem
     Angebot heraus angelegt, und ein neuer Baustein soll nicht den
     ganzen Stammbestand neu holen. */
  textbausteine: (art?: string, nurAktive?: boolean, beleg?: string) =>
    ["textbausteine", art ?? "", nurAktive ? "aktiv" : "", beleg ?? ""] as const,
  /** Alle Fenster der Bausteine — zum Auffrischen, nicht zum Lesen. */
  textbausteineAlle: ["textbausteine"] as const,
  bausteinverlauf: (id: string) => ["textbaustein", id, "verlauf"] as const,
  geraetegruppen: ["stamm", "gruppen"] as const,
  kalkulation: ["stamm", "kalkulation"] as const,
  maschine: (id: string) => ["stamm", "maschine", id] as const,
  /* Unter `stamm`, damit jede Stammdatenänderung sie mitnimmt: Eine neue
     Maschine eines Zubehörtyps ist ein Stück mehr in der Auswahl. */
  zubehoerTypen: ["stamm", "zubehoer", "typen"] as const,
  zubehoerAuswahl: (typ: string, von: string, bis: string) =>
    ["stamm", "zubehoer", "auswahl", typ, von, bis] as const,
  /* Was zu einem Gerät gehört: Stammfahrer und Auflieger, beide mit der
     Auskunft, ob sie im Zeitraum frei sind. Unter `stamm`, damit ein neuer
     Stammfahrer oder ein neues Gespann sie mitnimmt — die Freiheit ändert
     sich allerdings mit jeder Belegung, darum steht sie nicht lange. */
  maschinenVorschlag: (id: string, von: string, bis: string) =>
    ["stamm", "maschine", id, "vorschlag", von, bis] as const,
  person: (id: string) => ["stamm", "person", id] as const,
  abrechnung: ["abrechnung"] as const,
  jobs: (art?: string) => ["jobs", art ?? ""] as const,
  /** Der Zeitplan — ein eigener Schlüssel, weil ihn kein Lauf ändert. */
  jobPlan: ["jobplan"] as const,
  preis: (
    typ?: string, von?: string, bis?: string, kunde?: string, bedient?: boolean, variante?: string,
    /** Die Zahl der Miettage, wenn statt eines Endes eine Dauer gefragt ist. */
    miettage?: number,
  ) =>
    ["preis", typ ?? "", von ?? "", bis ?? "", kunde ?? "", bedient ? "bedient" : "", variante ?? "",
      miettage ?? 0] as const,
  einstellungen: ["einstellungen"] as const,
  /* Unter `einstellungen`, damit eine geänderte Regel die Liste mitnimmt (0138). */
  vorbereitungsstand: ["einstellungen", "vorbereitung"] as const,
  /* Nicht unter „einstellungen": Das Formular liest sie in jeder Rolle, und
     die Einstellungen selbst holt nur die Verwaltung. Aufgefrischt wird sie
     trotzdem, wenn dort gespeichert wird (`useEinstellungenAendern`). */
  pflichtfelder: ["pflichtfelder"] as const,
  lexZugang: ["einstellungen", "lexware"] as const,
  /** Bewusst NICHT unter „einstellungen": Wer dort etwas ändert, verwirft den
      ganzen Zweig — und die Vorschau kostet Anfragen an Lexware. */
  lexWechsel: ["lexwechsel"] as const,
  benutzer: ["benutzer"] as const,
  /* Die Rollentafel steht NICHT unter `benutzer`: Sie hängt an keinem
     einzelnen Menschen, und ein Passwortwechsel soll sie nicht neu holen.
     Umgekehrt zieht eine Änderung an ihr die Benutzerliste nach — dort
     stehen die gerechneten Rechte jedes Menschen (0150). */
  rollen: ["rollen"] as const,
  farben: ["farben"] as const,
  nummern: ["einstellungen", "nummern"] as const,
  defekte: (nurOffene: boolean) => ["defekte", nurOffene] as const,
  angebotsvorgaben: ["angebote", "vorgaben"] as const,
  aufnahmen: ["aufnahmen"] as const,
  /* Die Wiedervorlagen (0111). Sie stehen für sich: Wer eine Notiz schreibt,
     ändert keinen Kunden — und wer sie abhakt, keine Notiz. */
  wiedervorlagen: (tage: number) => ["wiedervorlagen", tage] as const,
  wiedervorlagenAlle: ["wiedervorlagen"] as const,
};

export const useStart = () => useQuery({ queryKey: schluessel.start, queryFn: R.start });

/**
 * Das Brett zu einem Zeitraum.
 *
 * `vorigeHalten`: Während die Antwort zu einem neuen Fenster unterwegs ist,
 * bleibt die alte stehen (`isPlaceholderData`). Das Brett braucht das beim
 * Blättern während eines Zugs — sonst verschwände die Zeile unter der Hand.
 * Wer es einschaltet, muss die alten Spalten selbst umrechnen: Sie zählen
 * vom Anfang des **alten** Fensters.
 */
export const usePlanung = (von?: string, bis?: string, vorigeHalten = false) =>
  useQuery({
    queryKey: schluessel.planung(von, bis),
    queryFn: () => R.planung(von, bis),
    ...(vorigeHalten ? { placeholderData: keepPreviousData } : {}),
  });

export const useVorgaenge = () =>
  useQuery({ queryKey: schluessel.vorgaenge, queryFn: () => R.vorgaengeListe() });

/**
 * Was dieser Kunde schon hatte — gefiltert an der **Nummer**, nicht am Namen.
 *
 * Gefragt wird nur nach Aufträgen: Wiederholt wird, was wirklich beauftragt
 * war. Ein abgelehntes Angebot und ein stornierter Vorgang sind kein
 * „beim letzten Mal".
 */
export const useVerlauf = (kundeNr: string) =>
  useQuery({
    queryKey: schluessel.verlauf(kundeNr),
    queryFn: () => R.vorgaengeListe("auftrag", kundeNr),
    enabled: !!kundeNr,
  });

/** Die Vorlage aus einem früheren Vorgang. Sie entsteht auf Zuruf, nicht auf Vorrat. */
export const useWiederholung = () =>
  useMutation({ mutationFn: (nr: string) => R.wiederholung(nr) });

export const useVorgang = (nr: string) =>
  useQuery({ queryKey: schluessel.vorgang(nr), queryFn: () => R.vorgang(nr), enabled: !!nr });

export const useProtokolle = (nr: string) =>
  useQuery({ queryKey: schluessel.protokolle(nr), queryFn: () => R.protokolle(nr), enabled: !!nr });

export const useAngebote = () =>
  useQuery({ queryKey: schluessel.angebote, queryFn: R.angeboteListe });

/* ══════════════ Die Ortsaufnahme ══════════════
   Jede Änderung frischt **beides** auf: die Liste der Aufnahmen und den
   Vorgang, an dem die Position hängt. Das Ergebnis schreibt die Menge in die
   Position — bleibt der Vorgang auf dem alten Stand, steht dort weiter
   „Warte auf Aufmaß", obwohl die Zahl längst da ist. */

export const useAufnahmen = () =>
  useQuery({ queryKey: schluessel.aufnahmen, queryFn: () => R.aufnahmen() });

function aufnahmeFrisch(k: ReturnType<typeof useQueryClient>, ausser?: string) {
  void k.invalidateQueries({ queryKey: schluessel.aufnahmen });
  /* Ein eben verworfener Vorgang wird nicht mitgefragt — er antwortet 404
     (N6-02, `daten/verworfen.ts`). */
  const filter = ausser === undefined ? {} : { predicate: ohneVorgang(ausser) };
  void k.invalidateQueries({ queryKey: ["vorgang"], ...filter });
  void k.invalidateQueries({ queryKey: ["positionen"], ...filter });
  // Die offenen Ortsaufnahmen stehen seit 0107 auch unter „Was ist zu tun?".
  void k.invalidateQueries({ queryKey: schluessel.start });
  // Und seit dem 19.09.2026 in der Tagesmappe und in der Liste am Telefon —
  // wer am Schreibtisch nimmt, soll sie dort nicht weiter im Pool sehen.
  void k.invalidateQueries({ queryKey: schluessel.handyHeute });
  void k.invalidateQueries({ queryKey: ["begehungen"] });
}

export function useAufnahmeBestellen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: (positionId: string) => R.aufnahmeBestellen(positionId),
    onSuccess: () => aufnahmeFrisch(k),
  });
}

export function useAufnahmeNehmen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, wer }: { id: string; wer?: string | null }) => R.aufnahmeNehmen(id, wer),
    onSuccess: () => aufnahmeFrisch(k),
  });
}

/**
 * **Den Termin setzen, verlegen oder streichen** (Migration 0180). Danach ist
 * mehr frisch als die Liste: Der Termin steht als Balken im Band der Person
 * (`planungAlle`) und in der Begehung, die ihn anzeigt (`["begehung", id]`).
 */
export function useAufnahmeTermin() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, termin }: { id: string; termin: { von: string; bis: string; person?: string } | null }) =>
      (termin ? R.aufnahmeTerminSetzen(id, termin) : R.aufnahmeTerminEntfernen(id)),
    onSuccess: (_a, e) => {
      aufnahmeFrisch(k);
      void k.invalidateQueries({ queryKey: schluessel.planungAlle });
      void k.invalidateQueries({ queryKey: ["begehung", e.id] });
    },
  });
}

export function useAufnahmeErgebnis() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, menge, notiz }: { id: string; menge: number; notiz?: string }) =>
      R.aufnahmeErgebnis(id, menge, notiz),
    onSuccess: () => aufnahmeFrisch(k),
  });
}

export function useAufnahmeVerwerfen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grund }: { id: string; grund?: string }) => R.aufnahmeVerwerfen(id, grund),
    onSuccess: () => aufnahmeFrisch(k),
  });
}

export const usePositionen = (nr: string) =>
  useQuery({ queryKey: schluessel.positionen(nr), queryFn: () => R.positionen(nr) });

export const useKunden = (
  suche?: string, nur?: "interessent" | "kunde" | "vermieter" | "deaktiviert", alle?: boolean,
) =>
  useQuery({
    queryKey: [...schluessel.kunden(suche, nur), alle ? "alle" : ""],
    queryFn: () => R.kunden(suche, nur, alle),
  });

export const useKunde = (nr: string) =>
  useQuery({ queryKey: schluessel.kunde(nr), queryFn: () => R.kunde(nr), enabled: !!nr });

/* ── Die Akte schreibt ──
   Jede Änderung frischt den Kunden UND die Liste auf: Die Liste zeigt Firma,
   Ort und Zahlungsziel, und eine Liste, die nach dem Speichern noch den alten
   Stand zeigt, sieht aus, als hätte das Speichern nicht gegriffen.

   Der Verlauf kommt mit, weil jede Änderung eine Zeile hineinschreibt. */
function useKundeFrisch() {
  const klient = useQueryClient();
  return (nr: string) => {
    void klient.invalidateQueries({ queryKey: schluessel.kunde(nr) });
    void klient.invalidateQueries({ queryKey: ["kunden"] });
    void klient.invalidateQueries({ queryKey: ["kundennotizen", nr] });
  };
}

export function useKundeAendern() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, felder }: { nr: string; felder: R.KundenFelder }) => R.kundeAendern(nr, felder),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

export const useKundennotizen = (nr: string) =>
  useQuery({
    queryKey: ["kundennotizen", nr] as const,
    queryFn: () => R.kundennotizen(nr),
    enabled: !!nr,
  });

/**
 * Eine Notiz schreiben — und im selben Griff die Wiedervorlage.
 *
 * Frisch wird danach beides: der Verlauf des Kunden und die Liste der
 * Wiedervorlagen. Eine Erinnerung, die erst nach einem Neuladen dasteht, ist
 * im Auto keine.
 */
export function useKundennotizAnlegen() {
  const klient = useQueryClient();
  return useMutation({
    mutationFn: ({ nr, text, art, wiedervorlageTage }: {
      nr: string; text: string; art?: string; wiedervorlageTage?: number;
    }) => R.kundennotizAnlegen(nr, text, art, wiedervorlageTage),
    onSuccess: (_, { nr }) => {
      void klient.invalidateQueries({ queryKey: ["kundennotizen", nr] });
      void klient.invalidateQueries({ queryKey: schluessel.wiedervorlagenAlle });
    },
  });
}

/* ══════════════ Die Wiedervorlage (Migration 0111) ══════════════
   Ein Datum am Kunden, das abgehakt wird. `tage` ist der Blick nach vorn;
   Überfälliges steht immer drin — eine Wiedervorlage, die von selbst aus dem
   Blick rutscht, ist keine. */

export const useWiedervorlagen = (vorlaufTage = 14) =>
  useQuery({
    queryKey: schluessel.wiedervorlagen(vorlaufTage),
    queryFn: () => R.wiedervorlagen(vorlaufTage),
  });

export function useWiedervorlageSetzen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (eingabe: Parameters<typeof R.wiedervorlageSetzen>[0]) =>
      R.wiedervorlageSetzen(eingabe),
    onSuccess: () => frisch(schluessel.wiedervorlagenAlle),
  });
}

export function useWiedervorlageErledigt() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.wiedervorlageErledigt(id),
    onSuccess: () => frisch(schluessel.wiedervorlagenAlle),
  });
}

export function useOrtAnlegen() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, ort }: { nr: string; ort: R.OrtEingabe }) => R.ortAnlegen(nr, ort),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

export function useOrtAendern() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, uuid, felder }: {
      nr: string; uuid: string; felder: Partial<R.OrtEingabe> & { aktiv?: boolean };
    }) => R.ortAendern(nr, uuid, felder),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

export function useOrtLoeschen() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, uuid }: { nr: string; uuid: string }) => R.ortLoeschen(nr, uuid),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

/**
 * Den Kontakt drüben anlegen — genau diesen einen.
 *
 * Danach ist die Akte neu zu holen: `drueben` steht anders da, und die Nummer
 * kann eine andere sein. Ob die Maske umziehen muss, entscheidet sie selbst;
 * hier wird nur der Stand frisch gemacht.
 */
export function useKundeNachLexware() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: (nr: string) => R.kundeNachLexware(nr),
    onSuccess: (_, nr) => frisch(nr),
  });
}

/* Die Ansprechpartner am Kunden. `usePersonAnlegen` weiter unten ist etwas
   anderes — die gehört zum Personal im Stamm, also zu einer Ressource, die
   auf dem Brett eine Spur hat. Hier geht es um den, den man beim Kunden
   anruft. Zwei Wörter für zwei Sachen, und darum `useKundenperson*`. */
export function useKundenpersonAnlegen() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, person }: { nr: string; person: R.PersonAmKunden }) =>
      R.kundenpersonAnlegen(nr, person),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

export function useKundenpersonAendern() {
  const frisch = useKundeFrisch();
  return useMutation({
    mutationFn: ({ nr, id, felder }: { nr: string; id: string; felder: Partial<R.PersonAmKunden> }) =>
      R.kundenpersonAendern(nr, id, felder),
    onSuccess: (_, { nr }) => frisch(nr),
  });
}

export const useMaschinen = () =>
  useQuery({ queryKey: schluessel.maschinen, queryFn: R.stammMaschinen });

export const usePersonal = () =>
  useQuery({ queryKey: schluessel.personal, queryFn: R.stammPersonal });

/** Die Vermieter — dieselben Geschaeftspartner, andere Rolle (Migration 0079). */
export const useVermieter = () =>
  useQuery({ queryKey: schluessel.vermieter, queryFn: R.stammVermieter });

export function useVermieterAnlegen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: (felder: { firma: string; ort?: string | null }) => R.vermieterAnlegen(felder),
    onSuccess: () => {
      void kunde.invalidateQueries({ queryKey: schluessel.vermieter });
      // Er ist ein Geschaeftspartner wie jeder andere -- die Kundenliste
      // fuehrt ihn ab sofort mit.
      void kunde.invalidateQueries({ queryKey: ["kunden"] });
    },
  });
}

export const useMaschinentypen = () =>
  useQuery({ queryKey: schluessel.maschinentypen, queryFn: R.stammMaschinentypen });

export const useZuschlaege = () =>
  useQuery({ queryKey: schluessel.zuschlaege, queryFn: R.stammZuschlaege });

/** `an = false` fragt nicht — etwa ein Blatt, das zu ist (A-02). */
export const useKataloge = (an = true) =>
  useQuery({ queryKey: schluessel.kataloge, queryFn: R.stammKataloge, enabled: an });

/* Die Abrechnung in drei Beständen, alle unter `["abrechnung"]` — eine
   Freigabe frischt so jeden davon auf, ohne ihn einzeln zu kennen. */
export const useAbrechnungsLaeufe = (status: "vorschlag" | "freigegeben") =>
  useQuery({ queryKey: [...schluessel.abrechnung, "laeufe", status], queryFn: () => R.abrechnungsLaeufe(status) });

export const useAbrechnungsLauf = (id: string | null) =>
  useQuery({
    queryKey: [...schluessel.abrechnung, "lauf", id ?? ""],
    queryFn: () => R.abrechnungsLauf(id as string),
    enabled: !!id,
  });

/** `nachziehen`: alle fünf Sekunden neu holen — solange eine Freigabe noch unterwegs ist (N5-04). */
export const useFreigegebeneBelege = (an: boolean, nachziehen = false) =>
  useQuery({
    queryKey: [...schluessel.abrechnung, "belege"],
    queryFn: R.freigegebeneBelege,
    enabled: an || nachziehen,
    refetchInterval: nachziehen ? 5000 : false,
  });

export const useJobs = (art?: string) =>
  useQuery({ queryKey: schluessel.jobs(art), queryFn: () => R.jobs(art) });

/**
 * Der Zeitplan. Seit dem 16.09.2026 lässt er sich im Blatt „Jobs" einstellen —
 * und er trägt den nächsten und den letzten Lauf. Beides veraltet mit der Uhr,
 * darum wird er jede Minute nachgeholt, solange jemand hinsieht.
 */
export const useJobPlan = () =>
  useQuery({ queryKey: schluessel.jobPlan, queryFn: R.jobPlan, refetchInterval: 60 * 1000 });

/* Beide setzen den Bestand direkt: Die Antwort ist schon der neue Plan. Die
   Historie wird mit frisch, weil ein angehaltener Lauf dort als Nächstes
   nichts mehr einträgt. */
export function useJobPlanSetzen() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: ({ art, teil }: { art: string; teil: Parameters<typeof R.jobPlanSetzen>[1] }) =>
      R.jobPlanSetzen(art, teil),
    onSuccess: (neu) => bestand.setQueryData(schluessel.jobPlan, neu),
  });
}

export function useJobPlanZurueck() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: (art: string) => R.jobPlanZurueck(art),
    onSuccess: (neu) => bestand.setQueryData(schluessel.jobPlan, neu),
  });
}

/**
 * Die Einstellungen des Betriebs — **nur gefragt, wer sie lesen darf**
 * (A-01, A-31, P-01, Durchgang 03.10.2026). `GET /api/einstellungen` hängt an
 * `verwalten`; Startseite und Brett fragten trotzdem bei jedem Laden und
 * hinterließen bei der Disposition eine 403 in der Konsole, die echte Fehler
 * verdeckt. Ohne das Recht bleibt `data` leer — genau wie vorher nach der 403.
 * `an = false` fragt zusätzlich gar nicht, auch mit dem Recht.
 */
export const useEinstellungen = (an = true) => {
  const { darfRecht } = useSitzung();
  return useQuery({
    queryKey: schluessel.einstellungen,
    queryFn: R.einstellungenLesen,
    enabled: an && darfRecht("verwalten"),
  });
};

/** Die Vorbereitungsregel des Betriebs und wer von ihr abweicht (0138). */
export const useVorbereitungsstand = () =>
  useQuery({ queryKey: schluessel.vorbereitungsstand, queryFn: R.vorbereitungsstand });

/** Was beim Anlegen Pflicht ist, je Rolle — für das Formular und das Einstellungsblatt. */
export const usePflichtfelder = () =>
  useQuery({ queryKey: schluessel.pflichtfelder, queryFn: R.pflichtfelder });

export const useBenutzer = () =>
  useQuery({ queryKey: schluessel.benutzer, queryFn: R.benutzer });

/* Die Kalenderfarben hängen länger als alles andere: Sie ändern sich
   im Jahr vielleicht einmal, und jede Maske mit einem Balken darin
   braucht sie. `staleTime` unendlich — was sie ändert, setzt den
   Bestand selbst. */
export const useKalenderfarben = () =>
  useQuery({ queryKey: schluessel.farben, queryFn: R.kalenderfarben, staleTime: Infinity });

export const useNummernkreise = () =>
  useQuery({ queryKey: schluessel.nummern, queryFn: R.nummernkreise });

/* Der Zustand der Lexware-Verbindung wird **nicht** von allein
   nachgeladen: Jeder Abruf, der den Zustand erneuert, kostet eine
   Anfrage drüben, und Lexware lässt zwei je Sekunde zu. Gespeichert
   ist er ohnehin — erneuert wird er, wenn jemand darum bittet. */
export const useLexZugang = () =>
  useQuery({ queryKey: schluessel.lexZugang, queryFn: R.lexZugang });

/* ══════════════ Was schreibt ══════════════
   Nach jeder Änderung wird der Bestand frisch geholt, nicht von Hand
   nachgezogen. Ein zweiter Stand in der Maske ist die Quelle, aus der
   „bei mir steht aber etwas anderes" kommt. */

/**
 * Der Preis zu Typ und Zeitraum.
 *
 * Steht eines von beiden nicht fest, wird nicht gefragt — eine Preisanfrage
 * ohne Zeitraum hat keine Antwort, und eine erfundene waere schlimmer als
 * keine.
 */
export function usePreis(
  typ?: string, von?: string | null, bis?: string | null, kunde?: string | null,
  /** Mit Bediener — die Zuschläge „nur mit/ohne Bediener" hängen daran (Migration 0096). */
  bedient?: boolean,
  /** Die Ausstattung — ohne sie findet ein Kran keinen Satz (16.09.2026). */
  variante?: string,
  optionen?: {
    /**
     * **Die letzte Antwort bleibt stehen, bis die neue da ist** — solange nur
     * Zeitraum oder Ausstattung wechseln (Durchgang 16.09.2026, PR6). Die
     * Preisauskunft zeigte bei jedem Tastendruck „lädt" statt des Preises.
     *
     * Eine andere Maschine, ein anderer Kunde oder die Frage nach dem Bediener
     * bekommen die alte Zahl **nicht**: Sie ist dann die Antwort auf eine
     * andere Frage. Wer hält, liest `isPlaceholderData` und sagt, dass die
     * Zahl gerade erneuert wird.
     */
    halten?: boolean;
    /**
     * Statt eines Endes eine Dauer: so viele **Miettage** ab `von`.
     *
     * Gerechnet wird das Ende drüben (`GET /preis?miettage=`), weil der
     * Mietkalender an Typ, Gruppe, Übergruppe und Betrieb hängt. Steht `bis`
     * daneben, gilt `bis` — zwei Angaben wären zwei Fragen.
     */
    miettage?: number;
  },
) {
  const nachDauer = !bis && Boolean(optionen?.miettage);
  const frage = schluessel.preis(
    typ, von ?? undefined, bis ?? undefined, kunde ?? undefined, bedient, variante,
    nachDauer ? optionen?.miettage : undefined,
  );
  return useQuery({
    queryKey: frage,
    queryFn: () => R.preis({
      typ: typ as string, von: von as string,
      ...(bis ? { bis } : { miettage: optionen?.miettage ?? 1 }),
      ...(kunde ? { kunde } : {}),
      ...(bedient ? { bedient } : {}),
      ...(variante ? { variante } : {}),
    }),
    enabled: Boolean(typ && von && (bis || nachDauer)),
    ...(optionen?.halten
      ? {
          placeholderData: (
            vorher: Awaited<ReturnType<typeof R.preis>> | undefined,
            vorige?: { queryKey: readonly unknown[] },
          ) => {
            // Stelle 1 ist der Typ, 4 der Kunde, 5 der Bediener (`schluessel.preis`).
            const alt = vorige?.queryKey;
            return alt && [1, 4, 5].every((i) => alt[i] === frage[i]) ? vorher : undefined;
          },
        }
      : {}),
  });
}

function useFrisch() {
  const kunde = useQueryClient();
  return (...schluessels: readonly (readonly unknown[])[]) =>
    schluessels.forEach((s) => void kunde.invalidateQueries({ queryKey: s }));
}

/**
 * Wie `useFrisch`, aber **mit Warten**: Das Versprechen erfüllt sich, wenn
 * die Abfragen neu geholt sind. Gibt eine Mutation es aus `onSuccess`
 * zurück, läuft der `onSuccess` der Maske erst danach — und das Blatt, das
 * sich dann schließt, zeigt schon den neuen Stand (N4-15).
 */
function useFrischAbwarten() {
  const kunde = useQueryClient();
  return (...schluessels: readonly (readonly unknown[])[]) =>
    Promise.all(schluessels.map((s) => kunde.invalidateQueries({ queryKey: s })));
}

export function useBelegungAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...rest }: { id: string } & Partial<Belegung> & { grund?: string }) =>
      R.belegungAendern(id, rest),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.start),
  });
}

export function useBelegungAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (neu: Omit<Belegung, "id">) => R.belegungAnlegen(neu),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.start),
  });
}

export function useBelegungLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, grund }: { id: string; grund: string }) => R.belegungLoeschen(id, grund),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.start),
  });
}

/**
 * Der Gerätetausch (docs/20-prozesse.md, C2).
 *
 * Er rührt an **drei** Stellen: dem Brett (zwei Balken statt einem), dem
 * Vorgang (die Ampel, wenn der Typ abweicht) und der Startseite (der Defekt
 * unter „Was hakt"). Deshalb werden alle drei frisch geholt — und der
 * Vorgang ausdrücklich mit, sonst steht die Ampel erst nach einem Neuladen da.
 */
export function useBelegungTauschen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: (tausch: Tausch) => R.belegungTauschen(tausch),
    onSuccess: () => {
      void kunde.invalidateQueries({ queryKey: schluessel.planungAlle });
      void kunde.invalidateQueries({ queryKey: schluessel.start });
      void kunde.invalidateQueries({ queryKey: ["vorgang"] });
      void kunde.invalidateQueries({ queryKey: ["defekte"] });
    },
  });
}

export const useDefekte = (nurOffene = true) =>
  useQuery({ queryKey: schluessel.defekte(nurOffene), queryFn: () => R.defekte(nurOffene) });

/**
 * Die zwei Knöpfe am Schaden (C6).
 *
 * Danach ist der Vorgang ein anderer: Bei „berechnen" hat er eine Position
 * mehr. Deshalb gehen Vorgang **und** Positionen frisch heraus.
 */
export function useSchadenEntscheiden() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: (eingabe: {
      protokollId: string;
      entscheidung: "kunde" | "betrieb";
      art: DefektArt;
      schwere: DefektSchwere;
      bezeichnung?: string;
      betrag?: number;
    }) => R.schadenEntscheiden(eingabe),
    onSuccess: () => {
      void kunde.invalidateQueries({ queryKey: ["vorgang"] });
      void kunde.invalidateQueries({ queryKey: ["positionen"] });
      void kunde.invalidateQueries({ queryKey: ["defekte"] });
      void kunde.invalidateQueries({ queryKey: schluessel.start });
    },
  });
}

export function useBelegungZurueck() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (bel: Belegung) => R.belegungZurueck(bel),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.start),
  });
}

export function useVorgangFreigeben() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (nr: string) => R.vorgangFreigeben(nr),
    /* „Was hier hakt" hängt an den Fällen (R-01, 03.10.2026): Ohne sie
       stand „Zur Abrechnung freigeben" bis zum Neuladen da. */
    onSuccess: (v) => frisch(schluessel.vorgang(v.nr), schluessel.vorgaenge, schluessel.start, ["faelle"] as const),
  });
}

/** „Leistung erledigt" — ein Auftrag ohne Gerät geht nach „Zurück" (P8-04). */
export function useVorgangErledigt() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (nr: string) => R.vorgangErledigt(nr),
    onSuccess: (v) => frisch(schluessel.vorgang(v.nr), schluessel.vorgaenge, schluessel.start, ["faelle"] as const),
  });
}

/** Ohne Rechnung abschließen (0280) — nur mit Grund, nur bei 0,00 offen. */
export function useVorgangOhneRechnung() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (e: { nr: string; grund: string }) => R.vorgangOhneRechnung(e.nr, e.grund),
    onSuccess: (_x, e) => frisch(schluessel.vorgang(e.nr), schluessel.vorgaenge, schluessel.start, ["faelle"] as const),
  });
}

export function useVorgangStornieren() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, grund, grundCode }: { nr: string; grund: string; grundCode?: string }) =>
      R.vorgangStornieren(nr, grund, grundCode),
    onSuccess: (v) => frisch(schluessel.vorgang(v.nr), schluessel.vorgaenge, schluessel.start),
  });
}

export function useAngebotVersenden() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (nr: string) => R.angebotVersenden(nr),
    /* Der Einzelsatz mit (Befund 26, 24.09.2026): Die Maske liest den
       Zustand aus ihm — ohne ihn stand nach dem Versenden weiter „Entwurf". */
    onSuccess: (_, nr) => frisch(schluessel.angebote, schluessel.planungAlle, schluessel.start,
      schluessel.vorgang(nr), schluessel.vorgaenge),
  });
}

export function useAngebotAnnehmen() {
  const frisch = useFrisch();
  return useMutation({
    /* Mit der Wahl des Kunden (optionale und alternative Positionen). Eine
       blanke Nummer nimmt an, was in der Summe stand. */
    mutationFn: (was: string | ({ nr: string; gewaehlt: string[] } & R.Annahmezusatz)) =>
      typeof was === "string"
        ? R.angebotAnnehmen(was)
        : R.angebotAnnehmen(was.nr, was.gewaehlt, {
          ...(was.kundeAngaben ? { kundeAngaben: was.kundeAngaben } : {}),
          ...(was.nachtraeglich ? { nachtraeglich: true } : {}),
          ...(was.bediener ? { bediener: was.bediener } : {}),
        }),
    /* Der Einzelsatz mit: Aus ihm liest der Schirm des Angebots den Zustand
       und führt nach der Annahme zum Auftrag hinüber. Ohne ihn stand dort
       weiter „versendet", bis jemand neu lud. */
    onSuccess: (_, was) =>
      frisch(schluessel.angebote, schluessel.planungAlle, schluessel.vorgaenge, schluessel.start,
        schluessel.vorgang(typeof was === "string" ? was : was.nr)),
  });
}

/** Was dem Kunden für die Rechnung fehlt — nur, solange das Annahmeblatt offen ist (F8). */
export function useAnnahmeLuecken(nr: string) {
  return useQuery({
    queryKey: ["vorgang", nr, "annahme-luecken"],
    queryFn: () => R.annahmeLuecken(nr),
    enabled: Boolean(nr),
    staleTime: 0,
  });
}

export function useAngebotAblehnen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, grund, grundCode }: { nr: string; grund: string; grundCode?: string }) =>
      R.angebotAblehnen(nr, grund, grundCode),
    onSuccess: (_, { nr }) => frisch(schluessel.angebote, schluessel.planungAlle, schluessel.start,
      schluessel.vorgang(nr)),
  });
}

/** Einen nie versendeten Entwurf verwerfen (B-12) — mit ihm gehen seine Vormerkungen aus dem Brett. */
export function useAngebotVerwerfen() {
  const frisch = useFrisch();
  const k = useQueryClient();
  return useMutation({
    /* **Weg ist weg** (AG-29, Durchgang 03.10.2026): Ein zweites `DELETE`
       nach einem Doppelklick bekam 404, und die Maske sagte „Vielleicht hat
       es jemand anders gelöscht" statt „verworfen". Ein 404 auf das eigene
       Verwerfen heißt: Der Entwurf ist nicht mehr da — das war das Ziel. */
    mutationFn: (nr: string) => R.angebotVerwerfen(nr).catch((e: unknown) => {
      if (schonWeg(e)) return;
      throw e;
    }),
    onSuccess: (_, nr) => {
      // Was zum Entwurf noch unterwegs ist, kommt als 404 zurück (N6-02).
      void vorgangStilllegen(k, nr);
      frisch(schluessel.angebote, schluessel.planungAlle, schluessel.start);
      // Eine nur bestellte Ortsaufnahme geht mit (Befund 40) — sie steht
      // danach nicht mehr offen in der Liste und nicht mehr im Band. Der
      // verworfene Vorgang selbst wird dabei nicht neu gefragt (N6-02).
      aufnahmeFrisch(k, nr);
    },
  });
}

/**
 * Nach dem Weggehen von einem verworfenen Vorgang: seine Abfragen aus dem
 * Bestand nehmen (N6-02). Erst danach — vorher holte die noch offene Seite
 * sie beim nächsten Zeichnen neu.
 */
export function useVorgangVergessen() {
  const k = useQueryClient();
  return (nr: string) => vorgangVergessen(k, nr);
}

export function useEinstellungenAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: R.einstellungenAendern,
    onSuccess: () => frisch(schluessel.einstellungen, schluessel.start, schluessel.pflichtfelder),
  });
}

/* Beide Lexware-Haken setzen den Bestand **direkt** statt ihn zu
   verwerfen: Die Antwort ist schon der neue Stand, und ein Nachladen
   wäre eine zweite Prüfung drüben für dieselbe Auskunft. */
export function useLexZugangSetzen() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.lexZugangSetzen,
    onSuccess: (neuerStand) => {
      bestand.setQueryData(schluessel.lexZugang, neuerStand);
      // Ein neuer Schlüssel kann einen Wechsel vormerken, auflösen oder auf
      // ein drittes Konto zeigen — eine Vorschau von vorhin gilt dann nicht.
      bestand.removeQueries({ queryKey: schluessel.lexWechsel });
      void bestand.invalidateQueries({ queryKey: schluessel.start });
    },
  });
}

/* ══════════════ Benutzer pflegen ══════════════

   Jeder dieser Haken holt die Liste frisch, statt sie von Hand
   nachzuziehen. Beim Anlegen und beim Sperren ändert sich mehr als die
   eine Zeile — ein Konto entsteht, Rollen fallen weg —, und ein zweiter
   Stand in der Maske ist die Quelle, aus der „bei mir steht aber etwas
   anderes" kommt. */

export function useBenutzerAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: R.benutzerAnlegen,
    onSuccess: () => frisch(schluessel.benutzer, schluessel.personal),
  });
}

export function useBenutzerAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, teil }: { id: string; teil: Parameters<typeof R.benutzerAendern>[1] }) =>
      R.benutzerAendern(id, teil),
    onSuccess: () => frisch(schluessel.benutzer, schluessel.personal, schluessel.ich),
  });
}

export function useBenutzerLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.benutzerLoeschen(id),
    // Auch das Personal: Der Mensch war eine Ressource im Kalender (Regel 8).
    onSuccess: () => frisch(schluessel.benutzer, schluessel.personal),
  });
}

/* ══════════════ Rollen und Rechte (0150) ══════════════

   Jede schreibende Route antwortet mit der ganzen Tafel — der Bestand wird
   deshalb gesetzt und nicht nur ungültig gemacht: Ein zweiter Abruf zeigte
   für einen Augenblick den alten Stand, und genau dort schaut jemand hin,
   der gerade einen Haken umgelegt hat.

   **Die Benutzerliste kommt mit.** Ein verstelltes Bündel ändert, was jeder
   mit dieser Rolle darf; `ich` ebenso, denn der Verwalter kann sich selbst
   betreffen (der Riegel lässt ihn nur nicht aussperren). */

export const useRollentafel = () =>
  useQuery({ queryKey: schluessel.rollen, queryFn: R.rollentafel });

function useRollenschreiber<A>(tun: (eingabe: A) => Promise<Rollentafel>) {
  const bestand = useQueryClient();
  const frisch = useFrisch();
  return useMutation({
    mutationFn: tun,
    onSuccess: (tafel) => {
      bestand.setQueryData(schluessel.rollen, tafel);
      frisch(schluessel.benutzer, schluessel.ich);
    },
  });
}

export function useRollenBuendel() {
  return useRollenschreiber(({ rolle, rechte }: { rolle: string; rechte: Recht[] }) =>
    R.rollenBuendelSetzen(rolle, rechte));
}

export function useRolleAnlegen() {
  return useRollenschreiber((eingabe: Parameters<typeof R.rolleAnlegen>[0]) =>
    R.rolleAnlegen(eingabe));
}

export function useRolleAendern() {
  return useRollenschreiber(({ code, teil }: {
    code: string; teil: Parameters<typeof R.rolleAendern>[1];
  }) => R.rolleAendern(code, teil));
}

export function useRolleLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.rolleLoeschen(code),
    onSuccess: () => frisch(schluessel.rollen, schluessel.benutzer, schluessel.ich),
  });
}

export function useBenutzerPasswort() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, passwort, passwortwechsel }: {
      id: string; passwort: string; passwortwechsel: Passwortwechsel;
    }) => R.benutzerPasswort(id, passwort, passwortwechsel),
    onSuccess: () => frisch(schluessel.benutzer),
  });
}

/* ══════════════ Die Kalenderfarben ══════════════

   Beide setzen den Bestand **direkt**: Die Antwort ist schon der neue
   Stand, und die Wurzelvariablen hängen daran. Ein Nachladen hieße,
   das Brett für einen Augenblick in der alten Farbe stehen zu lassen. */

export function useKalenderfarbenSetzen() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.kalenderfarbenSetzen,
    onSuccess: (neu) => bestand.setQueryData(schluessel.farben, neu),
  });
}

export function useKalenderfarbenZurueck() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.kalenderfarbenZurueck,
    onSuccess: (neu) => bestand.setQueryData(schluessel.farben, neu),
  });
}

export function useNummernkreisAendern() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: ({ art, teil }: { art: string; teil: Parameters<typeof R.nummernkreisAendern>[1] }) =>
      R.nummernkreisAendern(art, teil),
    onSuccess: (neu) => bestand.setQueryData(schluessel.nummern, neu),
  });
}

/* ══════════════ Der Wechsel auf ein anderes Lexware-Konto ══════════════

   Die Vorschau rechnet gegen das neue Konto und fragt dafür alle Kontakte
   und Artikel ab. Darum kein Nachladen beim Zurückkehren ins Fenster und
   keine Wiederholung bei einem Fehler: Ein abgelehnter Schlüssel wird beim
   dritten Versuch nicht richtiger, er drosselt nur. */

export const useLexWechselVorschau = (aktiv: boolean) =>
  useQuery({
    queryKey: schluessel.lexWechsel,
    queryFn: R.lexWechselVorschau,
    enabled: aktiv,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

export function useLexWechselFreigeben() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.lexWechselFreigeben,
    onSuccess: (neuerStand) => {
      bestand.setQueryData(schluessel.lexZugang, neuerStand);
      bestand.removeQueries({ queryKey: schluessel.lexWechsel });
      void bestand.invalidateQueries({ queryKey: schluessel.start });
    },
  });
}

export function useLexWechselVerwerfen() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.lexWechselVerwerfen,
    onSuccess: (neuerStand) => {
      bestand.setQueryData(schluessel.lexZugang, neuerStand);
      bestand.removeQueries({ queryKey: schluessel.lexWechsel });
      void bestand.invalidateQueries({ queryKey: schluessel.start });
      /* Ein Kontowechsel ändert, was „Was hier hakt" und die Glocke über
         Lexware Office sagen — beide lesen `faelle`. */
      void bestand.invalidateQueries({ queryKey: ["faelle"] });
      void bestand.invalidateQueries({ queryKey: ["vorgang"] });
    },
  });
}

export function useLexZugangPruefen() {
  const bestand = useQueryClient();
  return useMutation({
    mutationFn: R.lexZugangPruefen,
    onSuccess: (neuerStand) => bestand.setQueryData(schluessel.lexZugang, neuerStand),
  });
}

/**
 * Freigeben — der Knopf, der bis zum 15.09.2026 nichts tat.
 *
 * Er hatte kein `onClick`. Luca: *„Wenn ich eine Abrechnung freigebe,
 * passiert nichts."* Er trug also einen Zustand, den niemand herstellen
 * konnte. Jetzt gibt er frei und traegt die Belege hinueber, in einem.
 */
export function useAbrechnungFreigeben() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.abrechnungFreigeben(id),
    // Der Vorgang mit: Er steht danach auf `abgerechnet` oder trägt einen
    // abgelehnten Beleg, und beides zeigt seine Seitenleiste.
    onSuccess: () => frisch(schluessel.abrechnung, ["vorgang"] as const, schluessel.start, ["jobs"] as const),
  });
}

/** Einen abgelehnten Beleg noch einmal schicken. */
export function useBelegUebertragen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.belegUebertragen(id),
    onSuccess: () => frisch(schluessel.abrechnung, ["vorgang"] as const, schluessel.start, ["jobs"] as const),
  });
}

/** Der Tageslauf von Hand. Danach steht der Vorschlag in der Maske. */
export function useAbrechnungStarten() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: () => R.abrechnungStarten(),
    onSuccess: () => frisch(schluessel.abrechnung, ["vorgang"] as const, schluessel.start, ["jobs"] as const),
  });
}

/**
 * Übergabe und Rücknahme — das Protokoll, aus dem abgerechnet wird.
 *
 * Es frischt viel auf, und das ist kein Übermaß: Ein Protokoll setzt das Ist
 * an der Belegung, stellt den Vorgang auf `laeuft` oder `zurueck`, und das
 * steht im Brett, im Auftrag mit seinen Protokollen, in der Auftragsliste und
 * auf der Startseite.
 */
export function useProtokollAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (e: R.Protokolleingabe) => R.protokollAnlegen(e),
    onSuccess: () => frisch(
      schluessel.planungAlle, schluessel.vorgaenge, ["vorgang"] as const,
      ["protokolle"] as const, schluessel.start,
      // Der Tag des Fahrers und die Fotos der Maschine kommen aus
      // denselben Protokollen. Ohne diese beiden steht die Zeile nach der
      // Übergabe noch unter RAUS — und der Fahrer tippt sie ein zweites Mal.
      schluessel.handyHeute, ["geraetefotos"] as const,
    ),
  });
}

/**
 * Der Tag des Fahrers: was hinausgeht, was zurueckkommt (09-mobile.md).
 *
 * **Ueber die Mappe** (0113): Der letzte Stand liegt auf dem Geraet, und im
 * Funkloch steht er wieder da — mit seiner Uhrzeit, damit niemand eine Liste
 * von gestern fuer heute haelt (`daten/mappe.ts`).
 */
export const useHandyHeute = () =>
  useQuery({
    queryKey: schluessel.handyHeute,
    queryFn: () => mitMappe("handy/heute", () => R.handyHeute()),
  });

/**
 * Die Fotos einer Maschine — aus ihren Protokollen, mit Zusammenhang.
 *
 * Sie liegen nicht doppelt: Die Maschinenakte liest denselben Bestand wie
 * das Protokoll, ueber die Belegung.
 */
export const useGeraetefotos = (ressourceId: string) =>
  useQuery({
    queryKey: schluessel.geraetefotos(ressourceId),
    queryFn: () => R.geraetefotos(ressourceId),
    enabled: !!ressourceId,
  });

/* ══════════════ Checklisten, Vorbereitung, Aufgaben (0107) ══════════════
   Nehmen und Erledigen frischen **Startseite und Tagesliste** auf: Beide
   lesen dieselben abgeleiteten Aufgaben, und eine genommene Übergabe, die
   am Handy noch als frei dasteht, nimmt ein Zweiter ein zweites Mal. */

export const useChecklisten = () =>
  useQuery({ queryKey: schluessel.checklisten, queryFn: V.checklisten });

export const useChecklisteGeltung = (typId: string) =>
  useQuery({
    queryKey: schluessel.checklisteGeltung(typId),
    queryFn: () => V.checklisteGeltung(typId),
    enabled: !!typId,
  });

export function useFrageAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (e: V.Frageneingabe) => V.frageAnlegen(e),
    onSuccess: () => frisch(schluessel.checklisten),
  });
}

export function useFrageAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & Parameters<typeof V.frageAendern>[1]) =>
      V.frageAendern(id, felder),
    onSuccess: () => frisch(schluessel.checklisten),
  });
}

export function useFragenSortieren() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (ids: string[]) => V.fragenSortieren(ids),
    onSuccess: () => frisch(schluessel.checklisten),
  });
}

/**
 * Das Vorbereitungsblatt — ueber die Mappe (0113).
 *
 * Die Fragen kommen aus dem Maschinentyp und stehen nur im Server. Wer das
 * Blatt einmal auf dem Hof geoeffnet hat, hat sie danach auf dem Geraet —
 * und kann in der Kiesgrube weiterarbeiten. Ohne das waere die Vorbereitung
 * offline ein leeres Blatt.
 */
export const useVorbereitung = (belegungId: string) =>
  useQuery({
    queryKey: schluessel.vorbereitung(belegungId),
    queryFn: () => mitMappe(`vorbereitung/${belegungId}`, () => V.vorbereitungsblatt(belegungId)),
    enabled: !!belegungId,
  });

export function useVorbereitungErledigen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ belegungId, ...e }: { belegungId: string } & V.Vorbereitungseingabe) =>
      V.vorbereitungErledigen(belegungId, e),
    onSuccess: () => frisch(["vorbereitung"] as const, schluessel.handyHeute, schluessel.start),
  });
}

/**
 * **Was an dieser Belegung hinausgeht** — Ausstattung, Anbauteile, Zubehör,
 * Bediener, Zeitraum und Ort (22.09.2026).
 *
 * Er steht im Übergabeblatt über der Unterschrift: Der Kunde zeichnet unter
 * das, was er bekommt, und nicht unter eine leere Fläche.
 */
export const useUebergabeinhalt = (belegungId: string) =>
  useQuery({
    queryKey: schluessel.uebergabeinhalt(belegungId),
    queryFn: () => mitMappe(`uebergabeinhalt/${belegungId}`, () => V.uebergabeinhalt(belegungId)),
    enabled: !!belegungId,
  });

/** Die Vorbereitung je Maschine eines Vorgangs — ab wann, vorgezogen, erledigt (0128). */
export const useVorbereitungenDesVorgangs = (vorgang: string) =>
  useQuery({
    queryKey: schluessel.vorbereitungenDesVorgangs(vorgang),
    queryFn: () => V.vorbereitungenDesVorgangs(vorgang),
    enabled: !!vorgang,
  });

/** Am Vorgang vorziehen: Die Vorbereitung steht danach sofort im Pool (0128). */
export function useVorbereitungAnstossen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (belegungId: string) => V.vorbereitungAnstossen(belegungId),
    onSuccess: () => frisch(["vorbereitung"] as const, schluessel.handyHeute, schluessel.start),
  });
}

export function useMangelErledigen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (belegungId: string) => V.mangelErledigen(belegungId),
    // Der Fall „Mangel entscheiden" geht mit (D-11) — Band am Auftrag und Startseite.
    onSuccess: () => frisch(["vorbereitung"] as const, schluessel.start, ["faelle"] as const),
  });
}

export function useAufgabeNehmen() {
  const frisch = useFrisch();
  return useMutation({
    // `grund` gilt nur beim Zurückgeben (0145) und ist freiwillig.
    mutationFn: ({ art, belegungId, wer, grund, bestaetigt }: {
      art: V.Belegungsaufgabe; belegungId: string; wer?: string | null; grund?: string | null;
      /** D-47: Hinweis auf die Qualifikation gelesen — trotzdem nehmen. */
      bestaetigt?: boolean;
    }) => V.aufgabeNehmen(art, belegungId, wer, grund, bestaetigt === true),
    onSuccess: () => frisch(["vorbereitung"] as const, schluessel.handyHeute, schluessel.start),
  });
}

/** Ein Protokoll korrigieren — mit Grund. Das Ist ändert sich, also dasselbe. */
export function useProtokollKorrigieren() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & Parameters<typeof R.protokollKorrigieren>[1]) =>
      R.protokollKorrigieren(id, felder),
    onSuccess: () => frisch(
      schluessel.planungAlle, ["vorgang"] as const, ["protokolle"] as const, schluessel.start,
    ),
  });
}

/**
 * Eine fehlende Unterschrift freigeben (0122) — Disposition und Verwaltung.
 *
 * Danach geht der Bericht ohne Unterschrift an den Kunden; die Aufgabe
 * `unterschrift` auf der Startseite ist erledigt.
 */
export function useUnterschriftFreigeben() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (protokollId: string) => R.unterschriftFreigeben(protokollId),
    onSuccess: () => frisch(["vorgang"] as const, ["protokolle"] as const, schluessel.start),
  });
}

/**
 * **Pflichtfotos, die draußen nicht gingen** (0173) — der Grund je
 * Blickwinkel an einer Belegung, für den Blick unter „Zu prüfen".
 */
export const useFotoauswege = (belegung: string | null) =>
  useQuery({
    queryKey: ["pflichtfotos", "fehlen", belegung ?? ""] as const,
    queryFn: () => R.fotoauswege(belegung ?? ""),
    enabled: !!belegung,
  });

/** Die Disposition hat die Gründe gelesen — die Zeile unter „Zu prüfen" geht. */
export function useFotoauswegeGesehen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (belegung: string) => R.fotoauswegeGesehen(belegung),
    onSuccess: () => frisch(["pflichtfotos"] as const, schluessel.start),
  });
}

/** Die Nachbereitung Nachtanken erledigen — oder, mit Grund, korrigieren (0172, 0279). */
export function useNachtanken() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (e: { protokollId: string; liter: number; grund?: string }) =>
      R.nachtankenEintragen(e.protokollId, e.liter, e.grund),
    // Auch „Was hier hakt" (die Fälle) — dort stand die Nachbereitung bis eben.
    // Und am Telefon die Mappe und der Einsatz (P8-02).
    onSuccess: () => frisch(
      ["vorgang"] as const, ["protokolle"] as const, ["faelle"] as const, schluessel.start,
      ["nachbereitung"] as const, ["handy"] as const,
    ),
  });
}

/** Die Nachbereitung an einem Einsatz, fürs Telefon (P8-02). */
export function useNachbereitungAmEinsatz(belegung: string) {
  return useQuery({
    queryKey: ["nachbereitung", belegung] as const,
    queryFn: () => R.nachbereitungAmEinsatz(belegung),
    enabled: !!belegung,
  });
}

/** Die Ist-Menge einer Position eintragen — „Menge fehlt" im Prüfkasten (06.10.2026). */
export function useIstMengeEintragen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (e: { positionId: string; metric: string; menge: number; grund?: string }) => R.istMengeEintragen(e),
    onSuccess: () => frisch(["vorgang"] as const, schluessel.start, ["aufwand"] as const),
  });
}

/** Die Aufwandspositionen eines Einsatzes, fürs Telefon (0280) — ohne Preis. */
export function useAufwandDerBelegung(belegung: string) {
  return useQuery({
    queryKey: ["aufwand", belegung] as const,
    queryFn: () => R.aufwandDerBelegung(belegung),
    enabled: !!belegung,
  });
}

/** Die Aufwandspositionen eines Auftrags, für den Prüfkasten am Schreibtisch — „Menge korrigieren". */
export function useAufwandDesVorgangs(nr: string, an = true) {
  return useQuery({
    queryKey: ["aufwand", "vorgang", nr] as const,
    queryFn: () => R.aufwandDesVorgangs(nr),
    enabled: !!nr && an,
  });
}

/** Einen Vorschlag verwerfen — der Gegenknopf zur Freigabe. */
export function useAbrechnungVerwerfen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.abrechnungVerwerfen(id),
    onSuccess: () => frisch(schluessel.abrechnung, ["vorgang"] as const, schluessel.start),
  });
}

export function useJobStarten() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (art: string) => R.jobStarten(art),
    // `schluessel.jobs()` allein trifft nur die ungefilterte Liste
    // (`["jobs", ""]`). Die Maske steht aber auf einer Art — und die hat
    // einen eigenen Schluessel. Ein Lauf lief dann, und die Liste davor
    // sagte weiter "noch kein Lauf verzeichnet".
    //
    // `["jobs"]` ohne zweiten Teil trifft als Praefix beide.
    onSuccess: () => frisch(["jobs"] as const, schluessel.start),
  });
}

/* ══════════════ Stammdaten pflegen (15.09.2026) ══════════════

   **Ein Stammdatum hängt selten allein.** Wer eine Maschine umbenennt,
   ändert die Zeilenbeschriftung im Brett; wer einen Tarif anlegt,
   ändert das Preisblatt; wer eine Qualifikation anlegt, ändert die
   Auswahl im Personalblatt. Darum frischt jede Änderung hier **mehr
   als eine Liste** auf — und zwar mit dem Präfix `["stamm"]`, der sie
   alle trifft.

   Der Grund steht schon oben bei `useJobStarten`: Ein zweiter Stand in
   der Maske ist die Quelle, aus der „bei mir steht aber etwas anderes"
   kommt. */

/** Alles unter `["stamm", …]` plus das Brett und die Startseite. */
const STAMM = ["stamm"] as const;

export const useGeraetegruppen = () =>
  useQuery({ queryKey: schluessel.geraetegruppen, queryFn: R.stammGruppen });

/**
 * Die Kostenseite. Nur Buchhaltung und Verwaltung dürfen sie sehen — wer
 * nicht darf, bekommt einen 403, und `Zustandsband` macht daraus den Satz
 * „kein Zugang" statt eines Fehlers.
 */
export const useKalkulation = () =>
  useQuery({ queryKey: schluessel.kalkulation, queryFn: R.kalkulation });

export const useMaschine = (id: string | null) =>
  useQuery({
    queryKey: schluessel.maschine(id ?? ""),
    queryFn: () => R.stammMaschine(id as string),
    enabled: !!id,
  });

export const usePerson = (id: string | null) =>
  useQuery({
    queryKey: schluessel.person(id ?? ""),
    queryFn: () => R.stammPerson(id as string),
    enabled: !!id,
  });

export function useMaschineAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.MaschineEingabe) =>
      R.maschineAendern(id, felder),
    // Auch die Planung: Die Zeilenbeschriftung im Brett ist `resource.label`,
    // und die zieht mit der Inventarnummer mit.
    onSuccess: () => frisch(STAMM, schluessel.planungAlle, schluessel.start),
  });
}

export function useMaschineAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.MaschineEingabe) => R.maschineAnlegen(felder),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle, schluessel.start),
  });
}

/* ══════════════ Zubehör und Prüfintervalle am Typ ══════════════ */

export const useZubehoerTypen = () =>
  useQuery({ queryKey: schluessel.zubehoerTypen, queryFn: R.zubehoerTypen });

/**
 * Was an einen Typ passt und im Zeitraum frei ist.
 *
 * Gefragt wird erst, wenn Typ und Zeitraum stehen — „ist ein Korb frei?" hat
 * ohne Zeitraum keine Antwort.
 */
export function useZubehoerAuswahl(typ?: string, von?: string | null, bis?: string | null) {
  return useQuery({
    queryKey: schluessel.zubehoerAuswahl(typ ?? "", von ?? "", bis ?? ""),
    queryFn: () => R.zubehoerAuswahl(typ as string, von as string, bis as string),
    enabled: Boolean(typ && von && bis),
  });
}

export function useZubehoerTypAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.ZubehoertypEingabe) => R.zubehoerTypAnlegen(felder),
    // Der neue Typ steht auch in der Typenauswahl der Maschinenakte und im Preisblatt.
    onSuccess: () => frisch(STAMM),
  });
}

export function useZubehoerTypAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.TypEingabe) => R.zubehoerTypAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZubehoerPassend() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, passend }: { id: string; passend: R.ZuordnungEingabe[] }) =>
      R.zubehoerPassendSetzen(id, passend),
    onSuccess: () => frisch(STAMM),
  });
}

/**
 * Die Traegergeraete eines Anbauteils — dieselbe Menge wie `passend`, von der
 * anderen Seite gepflegt (Luca, 16.09.2026: *„dann muss man das ja auch bei
 * den Anbauteilen schon sagen koennen, woran das passt"*).
 *
 * **Je Traeger ein Aufruf** (Nachpruefung P6): erst die neuen dazu, dann die
 * entfernten weg — die uebrigen Zuordnungen fasst keiner an.
 */
export function useZubehoerTraeger() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: async ({ id, dazu, weg }: { id: string; dazu: string[]; weg: string[] }) => {
      for (const traeger of dazu) await R.zubehoerTraegerDazu(id, traeger);
      for (const traeger of weg) await R.zubehoerTraegerWeg(id, traeger);
    },
    // Auch nach einem Fehler mittendrin: Was schon durch ist, soll die Maske sehen.
    onSettled: () => frisch(STAMM),
  });
}

/** Die Auflieger einer Zugmaschine — als ganze Menge (Migration 0072). */
export function useZubehoerGespann() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, gespann }: { id: string; gespann: R.GespannEingabe[] }) =>
      R.zubehoerGespannSetzen(id, gespann),
    onSuccess: () => frisch(STAMM),
  });
}

/**
 * Was beim Disponieren zu einem Gerät gehört: Stammfahrer und Auflieger.
 *
 * Gefragt wird erst, wenn Gerät und Zeitraum stehen — „ist der Meyer frei?"
 * hat ohne Zeitraum keine Antwort. Dieselbe Bedingung wie beim Zubehör.
 */
export function useMaschinenVorschlag(id?: string | null, von?: string | null, bis?: string | null) {
  return useQuery({
    queryKey: schluessel.maschinenVorschlag(id ?? "", von ?? "", bis ?? ""),
    queryFn: () => R.maschinenVorschlag(id as string, von as string, bis as string),
    enabled: Boolean(id && von && bis),
  });
}

export function usePersonAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.PersonEingabe) =>
      R.personAendern(id, felder),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle, schluessel.start),
  });
}

export function usePersonAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.PersonEingabe) => R.personAnlegen(felder),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle, schluessel.start),
  });
}

export function useTarifAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.TarifEingabe) => R.tarifAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useTarifAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.TarifEingabe) => R.tarifAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useTarifLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.tarifLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZuschlagAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ code, ...felder }: { code: string } & R.ZuschlagEingabe) =>
      R.zuschlagAendern(code, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/** Eine Zuschlagsregel anlegen. Die Route gab es; den Knopf dazu erst seit dem 16.09.2026. */
export function useZuschlagAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.ZuschlagEingabe & { code: string }) => R.zuschlagAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Leistungsarten ──────────────────────────────────────────────────
   Der Katalog hinter der Position „Leistung". Jede Änderung frischt
   `["stamm"]` auf und trifft damit auch `/stamm/kataloge` — dieselbe
   Liste wird dort mitgeliefert, und zwei Stände derselben Liste sind
   die Quelle für „bei mir steht aber etwas anderes". */

export const useLeistungsarten = () =>
  useQuery({ queryKey: schluessel.leistungsarten, queryFn: R.leistungsarten });

/** Der Warenkatalog — für die Position „Ware" und für das Pflegeblatt. */
export const useWaren = () =>
  useQuery({ queryKey: schluessel.waren, queryFn: R.waren });

/* ── Textbausteine (Migration 0058) ─────────────────────────── */

/**
 * Die gespeicherten Freitexte — für den Schnellzugriff und für das Blatt.
 *
 * Der Schnellzugriff fragt nach Art und nur nach den aktiven, das Blatt nach
 * allem. Zwei Fenster desselben Bestands, und `textbausteineAlle` ist das
 * Präfix, mit dem beide zugleich frisch werden.
 */
export const useTextbausteine = (art?: Bausteinart, nurAktive = false, beleg?: Belegart) =>
  useQuery({
    queryKey: schluessel.textbausteine(art, nurAktive, beleg),
    queryFn: () => R.textbausteine(art, nurAktive, beleg),
  });

/* ── Die Texte je Belegart am Vorgang (Migration 0105) ────────── */

/** Alle vier Belegarten eines Vorgangs, aufgelöst — mit dem Standard als Platzhalter. */
export const useBelegtexte = (nr: string) =>
  useQuery({ queryKey: schluessel.belegtexte(nr), queryFn: () => R.belegtexte(nr), enabled: !!nr });

/** Abweichende Texte einer Belegart schreiben — die Antwort ist der neue Stand. */
export function useBelegtexteSchreiben() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: ({ nr, art, felder }: {
      nr: string;
      art: Exclude<Belegart, "angebot">;
      felder: { vortext?: string | null; nachtext?: string | null };
    }) => R.belegtexteSchreiben(nr, art, felder),
    onSuccess: (stand, { nr }) => kunde.setQueryData(schluessel.belegtexte(nr), stand),
  });
}

/** Der Verlauf eines Bausteins — erst, wenn jemand ihn aufklappt. */
export const useBausteinverlauf = (id: string) =>
  useQuery({
    queryKey: schluessel.bausteinverlauf(id),
    queryFn: () => R.textbausteinVerlauf(id),
    enabled: !!id,
  });

export function useTextbausteinAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.BausteinEingabe & { stichwort: string; text: string }) =>
      R.textbausteinAnlegen(felder),
    onSuccess: () => frisch(schluessel.textbausteineAlle, ["belegtexte"]),
  });
}

export function useTextbausteinAendern() {
  const frisch = useFrisch();
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.BausteinEingabe) =>
      R.textbausteinAendern(id, felder),
    onSuccess: () => {
      // Die Texte an den Vorgängen mit: Ein neuer Standard ist dort der neue Platzhalter.
      frisch(schluessel.textbausteineAlle, ["belegtexte"]);
      // Der Verlauf ist um eine Zeile länger — und wer gerade geschrieben hat,
      // ist der, der ihn sich ansieht. Frisch wird er für ALLE Bausteine:
      // Wird einer zum Standard, verliert ein zweiter ihn, und dessen Verlauf
      // hat dann ebenfalls eine Zeile mehr.
      void kunde.invalidateQueries({ queryKey: ["textbaustein"] });
    },
  });
}

/**
 * Den Zähler hochsetzen — abgeschickt, nicht abgewartet.
 *
 * Kein `useMutation`: Es gibt nichts zu warten, nichts zu melden und nichts,
 * was bei einem Fehlschlag anders wäre. Die Liste wird trotzdem frisch, damit
 * die Zahl im Blatt stimmt, wenn jemand nachsieht.
 */
export function useBausteinBenutzt(): (id: string) => void {
  const frisch = useFrisch();
  return (id: string) => {
    void R.textbausteinBenutzt(id).then(() => frisch(schluessel.textbausteineAlle));
  };
}

export function useWareAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.WareEingabe & { bezeichnung: string; einheit: string }) => R.wareAnlegen(felder),
    onSuccess: () => frisch(schluessel.waren),
  });
}

export function useWareAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.WareEingabe) => R.wareAendern(id, felder),
    onSuccess: () => frisch(schluessel.waren),
  });
}

export function useLeistungsartAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.LeistungsartEingabe & { code: string }) => R.leistungsartAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useLeistungsartAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.LeistungsartEingabe) =>
      R.leistungsartAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/* `useFaktorAnlegen`, `useFaktorAendern` und `useFaktorLoeschen` standen hier
   bis zum 21.09.2026 — die Pflege der Preistreiber. Die Gattung ist abgebaut
   (Migration 0164). */

/* Die Preiskategorien und ihre Sätze (Migration 0142, G9) — der
   Verschmutzungsgrad. Sie stehen in derselben Maske und frischen dieselbe
   Liste auf. */

export function useKategorieAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ leistungId, ...felder }:
      { leistungId: string } & R.KategorieEingabe & { code: string; bezeichnung: string }) =>
      R.kategorieAnlegen(leistungId, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useKategorieAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.KategorieEingabe) =>
      R.kategorieAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useKategorieLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.kategorieLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMengensatzAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ leistungId, ...felder }:
      { leistungId: string } & R.MengensatzEingabe & { ab_menge: number; netto: number }) =>
      R.mengensatzAnlegen(leistungId, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMengensatzAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.MengensatzEingabe) =>
      R.mengensatzAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMengensatzLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.mengensatzLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Leistungszuschlaege (Migration 0152) ────────────────────────────
   Der Zusatz, der zum Verschmutzungsgrad DAZUKOMMT -- Flechten. Dieselben
   Haken wie an der Kategorie daneben, weil sie in derselben Maske stehen und
   dieselbe Liste auffrischen. */

export function useLeistungszuschlagAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ leistungId, ...felder }:
      { leistungId: string } & R.LeistungszuschlagEingabe & { code: string; bezeichnung: string }) =>
      R.leistungszuschlagAnlegen(leistungId, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useLeistungszuschlagAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.LeistungszuschlagEingabe) =>
      R.leistungszuschlagAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useLeistungszuschlagLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.leistungszuschlagLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZuschlagssatzSetzen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string; kategorie: string | null; netto: number | null }) =>
      R.zuschlagssatzSetzen(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useKalkulationSetzen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.KostenEingabe) => R.kalkulationSetzen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useKalkulationLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.kalkulationLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Personaltarife ──────────────────────────────────────────────── */

export function usePersonaltarifAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.PersonaltarifEingabe & { code: string }) => R.personaltarifAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function usePersonaltarifAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.PersonaltarifEingabe) => R.personaltarifAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function usePersonaltarifLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.personaltarifLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Die Tarifkette am Typ ──────────────────────────────────────────
   Umhängen, eigene Sätze anlegen, eigene Sätze löschen. Jede dieser
   Änderungen bewegt, woher andere Zeilen ihren Preis haben — darum frischt
   sie alles unter `["stamm"]` auf, auch die Gruppen mit ihrer `wirkung`. */

/** Zählen statt einzeln führen (0194) — danach stehen Stamm und Brett anders da. */
export function useBestandSetzen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, bestand }: { id: string; bestand: number | null }) =>
      R.maschinentypBestandSetzen(id, bestand),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle),
  });
}

export function useMaschinentypAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: {
      id: string; bezeichnung?: string; gruppe_id?: string; preismodell?: string;
      mietkalender?: string | null; aktiv?: boolean;
      gewicht_kg?: number | null; laenge_mm?: number | null;
      breite_mm?: number | null; hoehe_mm?: number | null;
      bedienung?: Bedienungsart | null;
      /** Hat der Typ einen abzulesenden Zähler (0151)? `null` heißt „nicht gesagt". */
      zaehler?: boolean | null;
    }) => R.maschinentypAendern(id, felder),
    // Auch die Planung: Wer einen Typ umbenennt, benennt die Zeile im Brett um.
    onSuccess: () => frisch(STAMM, schluessel.planungAlle),
  });
}

export function useEigeneTarifeAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (typId: string) => R.eigeneTarifeAnlegen(typId),
    onSuccess: () => frisch(STAMM),
  });
}

export function useEigeneTarifeLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (typId: string) => R.eigeneTarifeLoeschen(typId),
    onSuccess: () => frisch(STAMM),
  });
}

export function useGruppeAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.GruppeEingabe) => R.gruppeAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useGruppeAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.GruppeEingabe) => R.gruppeAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Der Baum der Preise: anlegen, umhängen, löschen (16.09.2026) ────
   Bis dahin ließ sich an keiner Ebene etwas Neues anlegen — es gab für
   den Maschinentyp nicht einmal eine Route. */

export function useGruppeLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.gruppeLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMaschinentypAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: { gruppe_id: string; bezeichnung: string; preismodell?: string }) =>
      R.maschinentypAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMaschinentypLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.maschinentypLoeschen(id),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle),
  });
}

/**
 * Der ganze Maschinentyp aus einem Formular (`MaschinentypPflege`).
 *
 * Auch das Brett und die Startseite: Ein neuer Typ ändert, was in der
 * Verfügbarkeit auftaucht, und ein abgeschalteter, was verschwindet.
 */
export function useMaschinentypSpeichern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, angelegt, ...felder }: {
      id: string | null;
      /** Meldet die Kennung eines neuen Typs, sobald es ihn gibt — auch wenn
       *  der zweite Schritt danach scheitert. */
      angelegt?: (id: string) => void;
    } & R.MaschinentypPflegeEingabe) =>
      R.maschinentypSpeichern(id, felder, angelegt),
    onSuccess: () => frisch(STAMM, schluessel.planungAlle, schluessel.start),
  });
}

export function useMietkalenderAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.MietkalenderEingabe) => R.mietkalenderAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useMietkalenderAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ code, ...felder }: { code: string } & R.MietkalenderEingabe) =>
      R.mietkalenderAendern(code, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useAbsagegrundAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.AbsagegrundEingabe) => R.absagegrundAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useAbsagegrundAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ code, ...felder }: { code: string } & R.AbsagegrundEingabe) =>
      R.absagegrundAendern(code, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Zahlungsziele (Migration 0106) ───────────────────────────────────
   Die Liste steht in den Katalogen; jeder Schreibweg frischt sie auf. */

export function useZahlungszielAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.ZahlungszielEingabe) => R.zahlungszielAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZahlungszielAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.ZahlungszielEingabe) =>
      R.zahlungszielAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZahlungszielLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.zahlungszielLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Notizarten (Migration 0146) ─────────────────────────────────────
   Die Auswahl über der Gesprächsnotiz. Sie steht in den Katalogen —
   und dort holt sie auch das Notizblatt am Telefon. */

export function useNotizartAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.NotizartEingabe) => R.notizartAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useNotizartAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & R.NotizartEingabe) =>
      R.notizartAendern(id, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/** Löschen geht nur, solange keine Notiz darauf zeigt — sonst stilllegen. */
export function useNotizartLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (id: string) => R.notizartLoeschen(id),
    onSuccess: () => frisch(STAMM),
  });
}

/* ── Drei Löschwege (16.09.2026) ─────────────────────────────────────
   Heiner (Betriebsleiter) bekam seine drei Probeeinträge nicht wieder
   weg. Gelöscht wird nur, was noch nichts getragen hat; alles andere
   wird abgeschaltet. */

export function useMietkalenderLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.mietkalenderLoeschen(code),
    onSuccess: () => frisch(STAMM),
  });
}

export function useAbsagegrundLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.absagegrundLoeschen(code),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZuschlagLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.zuschlagLoeschen(code),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZaehlgroesseAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.ZaehlgroesseEingabe) => R.zaehlgroesseAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useZaehlgroesseAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ code, ...felder }: { code: string } & R.ZaehlgroesseEingabe) =>
      R.zaehlgroesseAendern(code, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/** Löschen geht nur, solange nichts damit gezählt hat — sonst 409. */
export function useZaehlgroesseLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.zaehlgroesseLoeschen(code),
    onSuccess: () => frisch(STAMM),
  });
}

export function useQualifikationAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (felder: R.QualifikationEingabe) => R.qualifikationAnlegen(felder),
    onSuccess: () => frisch(STAMM),
  });
}

export function useQualifikationAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ code, ...felder }: { code: string } & R.QualifikationEingabe) =>
      R.qualifikationAendern(code, felder),
    onSuccess: () => frisch(STAMM),
  });
}

/** Löschen geht nur, solange niemand sie trägt und nichts sie verlangt — sonst 409. */
export function useQualifikationLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (code: string) => R.qualifikationLoeschen(code),
    onSuccess: () => frisch(STAMM),
  });
}

/**
 * Die Schnellvermietung — fünf Aufrufe hinter einem Knopf.
 *
 * Danach ist alles frisch, was den neuen Auftrag zeigt: das Brett (ein Balken
 * mehr), die Auftragsliste und die Startseite. Wer das vergisst, sieht nach
 * dem Vermieten dieselbe leere Woche wie vorher und drückt noch einmal.
 */
export function useSchnellVermieten() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (auftrag: R.Schnellauftrag) => R.schnellVermieten(auftrag),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.vorgaenge, schluessel.start),
  });
}

/* ══════════════ Der Abschluss der Merkliste (17.09.2026) ══════════════

   Zwei Knöpfe, ein Weg bis zum letzten Schritt (`daten/merkliste.ts`). Der
   eine legt den angenommenen Auftrag an, der andere den Entwurf, den die
   Angebotsmaske vorausgefüllt aufnimmt. Was aufgefrischt wird, ist beim
   Auftrag dasselbe wie bei der Schnellerfassung — ein neuer Balken im Brett
   ist ein neuer Balken im Brett. */

export function useMerklisteVermieten() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (abschluss: R.MerklisteAbschluss) => R.merklisteVermieten(abschluss),
    onSuccess: () => frisch(schluessel.planungAlle, schluessel.vorgaenge, schluessel.start),
  });
}

export function useMerklisteAngebot() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (abschluss: R.MerklisteAbschluss) => R.merklisteAngebot(abschluss),
    // Auch das Brett: Jede Mietposition merkt ihre Maschine vor.
    onSuccess: () => frisch(schluessel.angebote, schluessel.vorgaenge, schluessel.planungAlle, schluessel.start),
  });
}

/* ══════════════ Ein Angebot bauen (15.09.2026) ══════════════

   **Ein Angebot ist ein Vorgang mit Positionen, und Positionen ändern
   sich.** Bis heute gab es dafür keinen einzigen Haken — die Maske hielt
   ihre Zeilen im Speicher und verlor sie beim Verlassen der Seite.

   Jeder dieser Haken frischt **zwei** Bestände auf: die Positionen des
   Vorgangs und die Angebotsliste. Die Liste trägt die Summe und die Zahl
   der Positionen; wer nur die Positionen nachlädt, sieht rechts einen
   neuen Betrag und links den alten. */

/** Die Vorbelegung — heute die Bindefrist. Sie ändert sich selten. */
export const useAngebotsvorgaben = () =>
  useQuery({
    queryKey: schluessel.angebotsvorgaben,
    queryFn: R.angebotVorgaben,
    staleTime: 5 * 60 * 1000,
  });

export function useAngebotAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (eingabe: R.AngebotEingabe) => R.angebotAnlegen(eingabe),
    onSuccess: () => frisch(schluessel.angebote, schluessel.vorgaenge, schluessel.start),
  });
}

/**
 * Der Cache eines eben angelegten Entwurfs, **bevor** die Maske auf seine
 * Nummer umschaltet (03.10.2026). Ohne ihn starten Vorgang und Positionen
 * unter dem neuen Schlüssel leer, und die Maske zeigt für einen Augenblick
 * das Lade-Skelett — genau das sah aus wie ein Neuladen der Seite. Die
 * Positionen sind bekannt (keine), der Vorgang wird einmal geholt; schlägt
 * das fehl, lädt die Maske ihn wie sonst auch.
 */
export function useEntwurfVorbelegen() {
  const kunde = useQueryClient();
  return async (nr: string) => {
    kunde.setQueryData(schluessel.positionen(nr), []);
    await kunde.prefetchQuery({ queryKey: schluessel.vorgang(nr), queryFn: () => R.vorgang(nr) });
  };
}

export function useAngebotKopfAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, ...teil }: { nr: string } & Parameters<typeof R.angebotKopfAendern>[1]) =>
      R.angebotKopfAendern(nr, teil),
    // Die Bindefrist ist der Verfall der Vormerkungen — er steht am Balken.
    onSuccess: (_, eingabe) => frisch(schluessel.angebote, schluessel.vorgang(eingabe.nr), schluessel.planungAlle),
  });
}

export function usePositionAnlegen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, ...eingabe }: { nr: string } & R.PositionEingabe) =>
      R.positionAnlegen(nr, eingabe),
    /* **Und das Brett.** Eine Mietposition bringt ihre Maschine mit —
       vorgemerkt im Entwurf, fest am Auftrag (dienste/belegung.ts,
       `maschineBinden`). Ohne `planungAlle` stünde der Balken erst nach dem
       nächsten Wochenwechsel da. */
    onSuccess: (_, eingabe) =>
      frisch(schluessel.positionen(eingabe.nr), schluessel.vorgang(eingabe.nr),
        schluessel.angebote, schluessel.vorgaenge, schluessel.planungAlle, schluessel.start),
  });
}

export function usePositionAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, id, ...teil }: { nr: string; id: string } & Parameters<typeof R.positionAendern>[2]) =>
      R.positionAendern(nr, id, teil),
    onSuccess: (_, eingabe) => {
      frisch(schluessel.positionen(eingabe.nr), schluessel.vorgang(eingabe.nr),
        schluessel.angebote, schluessel.vorgaenge);
      /* Wer als Bediener gebunden oder gelöst wird, steht im Brett anders da
         — und ein neuer Zeitraum verschiebt seinen Balken mit. */
      if ("bediener_id" in eingabe || "bedient" in eingabe || "von" in eingabe || "bis" in eingabe
        || "zubehoer" in eingabe) {
        frisch(schluessel.planungAlle);
      }
      /* Und die Zubehörauskunft: Wer ein Stück löst, macht es frei — wer eins
         bindet, belegt es. Ohne das stünde daneben weiter „belegt bis …" und
         das eben freigewordene Stück ließe sich nicht wieder wählen
         (18.09.2026, im Browser gesehen). Sie hängt unter `stamm`, also
         genügt der Ast. */
      if ("zubehoer" in eingabe) frisch(["stamm", "zubehoer"]);
      /* Und die Verfügbarkeit des Typs: Wer das Stück wechselt, macht das alte
         frei und das neue belegt. Ohne das blieb die Wahl auf dem Stand von
         vorher stehen — und verschwand, weil das eben gewählte Stück nach der
         alten Auskunft das einzige war (18.09.2026, im Browser gesehen). */
      if ("maschine_id" in eingabe || "von" in eingabe || "bis" in eingabe || "zubehoer" in eingabe) {
        frisch(["verfuegbarkeit"]);
      }
    },
  });
}

/**
 * Wer diese Miete fahren kann (dienste/bediener.ts). Gefragt wird erst, wenn
 * die Auswahl aufgeht — die Liste prüft Belegung und Qualifikation je Mensch.
 */
export function useBedienerauswahl(nr: string, posId: string | undefined, aktiv: boolean) {
  return useQuery({
    queryKey: schluessel.bedienerauswahl(nr, posId ?? ""),
    queryFn: () => R.bedienerauswahl(nr, posId as string),
    enabled: aktiv && Boolean(nr && posId),
    staleTime: 15_000,
  });
}

export function usePositionLoeschen() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, id }: { nr: string; id: string }) => R.positionLoeschen(nr, id),
    // Die Vormerkung geht mit der Zeile (cascade) — also auch aus dem Brett.
    onSuccess: (_, eingabe) =>
      frisch(schluessel.positionen(eingabe.nr), schluessel.vorgang(eingabe.nr),
        schluessel.angebote, schluessel.vorgaenge, schluessel.planungAlle, schluessel.start),
  });
}

/**
 * Die neue Fassung — ein versendetes Angebot geht zurück in den Entwurf.
 *
 * Auch das Brett geht frisch heraus: Die Option bleibt zwar stehen, aber
 * die Kontur am Balken folgt der Phase des Vorgangs.
 */
export function useAngebotNeueFassung() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: (nr: string) => R.angebotNeueFassung(nr),
    /* **Der Einzelsatz mit** (Systemprüfung, Befund 26, 24.09.2026). Die
       Maske des Angebots liest ihren Zustand aus dem Vorgang, und der blieb
       im Zwischenspeicher auf „angebot": Nach „Fassung 2 erstellen" stand
       weiter das Band „NUR LESEN", und die Positionen ließen sich nicht
       ändern, bis jemand über die Liste neu hineinging. Und die neue
       Bindefrist (Befund 22) steht auch nur im Einzelsatz. */
    onSuccess: (_, nr) =>
      frisch(schluessel.angebote, schluessel.positionen(nr), schluessel.vorgang(nr),
        schluessel.vorgaenge, schluessel.planungAlle, schluessel.start),
  });
}

/**
 * **Eine begonnene Fassung verwerfen** (Befund 26) — zurück auf den Stand des
 * letzten versendeten Angebots. Positionen, Vormerkungen und Bindefrist
 * kommen zurück; darum geht alles frisch heraus, was sie zeigt.
 */
export function useFassungVerwerfen() {
  const frisch = useFrisch();
  const abwarten = useFrischAbwarten();
  return useMutation({
    mutationFn: (nr: string) => R.fassungVerwerfen(nr),
    /* **Erst neu geholt, dann geschlossen** (N4-15, Nachprüfung Runde 4):
       Das Blatt am Telefon zeigte drei Sekunden nach dem Verwerfen noch
       „Fassung 3 in Arbeit" — es las Liste und Vorgang aus dem Zwischen-
       speicher, und der Abruf lief noch. Auf Liste und Vorgang wird
       gewartet; der Rest frischt im Hintergrund. */
    onSuccess: async (_, nr) => {
      frisch(schluessel.vorgaenge, schluessel.planungAlle, schluessel.start);
      await abwarten(schluessel.angebote, schluessel.positionen(nr), schluessel.vorgang(nr));
    },
  });
}

/**
 * **Einen Beleg öffnen** — Angebot oder Auftragsbestätigung des Vorgangs,
 * im eigenen Reiter (06.10.2026, `daten/papieroeffnen.ts`). Bis dahin legte
 * dieser Haken die Datei in den Download-Ordner; Luca: *„Mach doch einfach
 * nur ‚Beleg öffnen'."*
 *
 * Beide liegen seit Phase 4 in Machine Office (die AB seit 0268); nur
 * Angebote von vor 0200 kommen noch von drüben. Den Lieferschein trägt das
 * Übergabeprotokoll.
 */
export function useBelegPdf() {
  return useMutation({
    mutationFn: ({ nr, art, beleg }: { nr: string; art: R.Papierart; beleg?: string }) =>
      papierOeffnen(() => R.belegPdf(nr, art, beleg)),
  });
}

/** **Wer hat was überarbeitet** (0202) — hängt unter dem Vorgang, frischt also mit ihm auf. */
export const useAngebotsfassungen = (nr: string, an = true) =>
  useQuery({
    queryKey: schluessel.angebotsfassungen(nr),
    queryFn: () => R.angebotsfassungenLesen(nr),
    enabled: an,
  });

/**
 * **Die Vorschau eines Angebots öffnen** (0200) — wie das Protokoll in einem
 * neuen Reiter, damit sie sich herunterladen und intern weitergeben lässt,
 * bevor das Angebot angelegt ist.
 */
export function useAngebotVorschau() {
  return useMutation({
    mutationFn: async (nr: string) => {
      const fenster = window.open("", "_blank");
      try {
        const datei = await R.angebotVorschau(nr);
        const weg = URL.createObjectURL(datei.blob);
        if (fenster) fenster.location.href = weg;
        else window.location.assign(weg);
        window.setTimeout(() => URL.revokeObjectURL(weg), 60_000);
      } catch (fehler) {
        fenster?.close();
        throw fehler;
      }
    },
  });
}

/**
 * **Ein Protokoll öffnen** (0198) — in einem neuen Reiter, damit es am
 * Telefon geteilt und am Schreibtisch gedruckt werden kann. Derselbe Weg wie
 * jeder Beleg (`daten/papieroeffnen.ts`); bekommt die Seite kein Fenster,
 * öffnet sie es im selben Reiter statt es herunterzuladen.
 */
export function useProtokollPdf() {
  return useMutation({
    mutationFn: (id: string) => papierOeffnen(() => R.protokollPdf(id)),
  });
}

/**
 * Das PDF einer Rechnung aus der Ablage — über eine signierte URL, die abläuft.
 *
 * Das Fenster geht **vor** dem Abruf auf: Ein `window.open` nach einem
 * `await` hält der Browser für ein Werbefenster und blockt es.
 */
export function useRechnungPdf() {
  return useMutation({
    mutationFn: async (pfad: string) => {
      const fenster = window.open("", "_blank");
      try {
        const url = await R.ablageUrl(pfad);
        if (fenster) fenster.location.href = url;
        else window.location.assign(url);
      } catch (fehler) {
        fenster?.close();
        throw fehler;
      }
    },
  });
}

/**
 * Was zu Typ und Zeitraum frei ist.
 *
 * **Ein Kran ohne freien Kranführer ist kein verfügbarer Kran** — die Antwort
 * ist darum ein Paar aus Lage und Vorschlägen, kein Ja. Gefragt wird erst,
 * wenn Typ und Zeitraum stehen; vorher gibt es nichts zu beantworten.
 */
export function useVerfuegbarkeit(
  typ?: string, von?: string | null, bis?: string | null, wenn = true,
  /** Stückzahl beim gezählten Bestand (0194) — sonst 1. */
  menge = 1,
) {
  return useQuery({
    queryKey: ["verfuegbarkeit", typ ?? "", von ?? "", bis ?? "", menge] as const,
    queryFn: () => R.verfuegbarkeit(typ as string, von as string, bis as string, menge),
    // `wenn`: Die Zeile im Angebot fragt nur, wenn sie die Wahl auch zeigt —
    // sonst lüde jede Positionsliste je Miete eine Auskunft (18.09.2026).
    enabled: Boolean(typ && von && bis) && wenn,
  });
}

/**
 * Betreff und Texte am Vorgang ändern.
 *
 * Frisch wird danach der Vorgang selbst und die Liste, in der er steht — der
 * Betreff ist dort die Überschrift, und eine Liste, die den alten zeigt, sieht
 * aus, als sei nichts gespeichert worden.
 */
export function useVorgangAendern() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, felder }: { nr: string; felder: R.VorgangsFelder }) =>
      R.vorgangAendern(nr, felder),
    // Die Texte des Angebots stehen am Vorgang — und im Stand je Belegart.
    onSuccess: (_, { nr }) => frisch(schluessel.vorgang(nr), schluessel.vorgaenge, schluessel.belegtexte(nr)),
  });
}

/** „Kunde holt ab" am Vorgang — die Zeilen und die Erwartung ändern sich mit. */
export function useAbholungSchalten() {
  const frisch = useFrisch();
  return useMutation({
    mutationFn: ({ nr, an }: { nr: string; an: boolean }) => R.abholungSchalten(nr, an),
    /* **Dieselben Abfragen wie nach einer Positionsänderung** (AG-42,
       Durchgang 03.10.2026): Das Blatt rechts liest das Netto aus der
       Angebotsliste. Ohne `angebote` stand dort nach „Kunde holt ab" weiter
       664,11, in der Tabelle 649,11 — zwei Zahlen für dasselbe Angebot. */
    onSuccess: (_, { nr }) => frisch(schluessel.vorgang(nr), schluessel.positionen(nr),
      schluessel.angebote, schluessel.vorgaenge),
  });
}

export const useVorgangsnotizen = (nr: string) =>
  useQuery({
    queryKey: ["notizen", nr] as const,
    queryFn: () => R.vorgangsnotizen(nr),
    enabled: !!nr,
  });

export function useNotizAnlegen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: ({ nr, text, art }: { nr: string; text: string; art?: string }) =>
      R.notizAnlegen(nr, text, art),
    /* Die Notiz steht auch im Verlauf (V-32, 03.10.2026) — ohne das zweite
       Auffrischen zeigte der Reiter den Stand vor der Notiz. */
    onSuccess: (_, { nr }) => {
      void kunde.invalidateQueries({ queryKey: ["notizen", nr] });
      void kunde.invalidateQueries({ queryKey: ["vorgaenge", "einsatzverlauf", nr] });
    },
  });
}
